//******************************************************************************
/**
 * @fileoverview ListPngParser - ListPNG解析クラス
 * @author KITAO Kaoru <kaoru@kitao.net>
 * @version 0.5.1
 * @since 2022-10-28
 * @copyright 2022 KITAO Kaoru
 * @license MIT
 * @requires DataPngBase
 * @requires RecordAnalyzer
 * * @history
 * - 2026-02-27 v0.5.1 package.json堅牢化
 * - 2026-02-24 v0.5.0 JSDoc関連精緻化
 * - 2026-02-03 v0.4.3 コメント・体裁調整
 * - 2026-01-29 v0.4.2 typo修正、headerチェックの厳密化
 * - 2026-01-28 v0.4.1 columnをRecordAnalyzerインスタンス化して格納手順を変更
 * - 2026-01-27 v0.4.0 名称変更（旧ListPng）、継承元クラスとの分離
 * - 2026-01-26 v0.3.0 データ取得メソッドの追加とメソッド名変更
 * - 2026-01-25 v0.2.0 subarray導入でガベージコレクションとメモリ浪費解消で改善
 * - 2022-10-28 v0.1.0 リリース
 */

//******************************************************************************
// 型定義
//------------------------------------------------------------------------------
/**
 * @typedef {Object} ColumnDefinition
 * @property {("undefined"|"rgba"|"uint"|"int")} recordType カラムのデータ型
 * @property {number} recordLength カラムあたりの占有ピクセル数
 * @property {number} offset オフセット値
 * @property {number} factor 係数常用対数
 * @property {Uint8Array|Uint8ClampedArray} rawData カラムが保持する全RGBA配列
 * @property {string} [name] カラム名
 */

/**
 * @typedef {Object} HeaderDefinition
 * @property {string} [name]
 * @property {number} pixelCount
 * @property {string} type
 * @property {number | Uint8Array | Uint8ClampedArray | RecordAnalyzer[] | undefined} [value]
 */

//******************************************************************************
// import
//------------------------------------------------------------------------------
// 継承元クラス
import { DataPngBase } from "./DataPngBase.js";
//------------------------------------------------------------------------------
// カラムデータ解析クラス
import { RecordAnalyzer } from "./RecordAnalyzer.js";

//******************************************************************************
/**
 * @extends DataPngBase
 */
class ListPngParser extends DataPngBase {
	//**************************************************************************
	/**
	 * 解析セッションごとの動的な状態を初期化
	 * @returns {this}
	 */
	initialize() {
		//----------------------------------------------------------------------
		// 親クラスのメソッドを実行
		super.initialize();
		//----------------------------------------------------------------------
		// rawDataを空にする
		this.rawData = null;
		// headersを空にする
		/** @type {HeaderDefinition[]} */
		this.headers = [];
		//----------------------------------------------------------------------
		// 参照を返す
		return this;
	}

	//**************************************************************************
	/**
	 * 新しい型付配列を与えてヘッダーを解析
	 * @param {Uint8Array|Uint8ClampedArray} rawData RGBAの型付配列
	 * @returns {this}
	 */
	parse(rawData) {
		//----------------------------------------------------------------------
		// 初期化して型付配列をセット
		this.initialize();
		this.rawData = rawData;
		//----------------------------------------------------------------------
		// 基本ヘッダー処理
		this.setBaseHeaders();
		// カラムヘッダー処理
		this.setColumnHeaders();
		// カスタムヘッダー処理
		this.setCustomHeaders();
		//----------------------------------------------------------------------
		// 処理が終わればポインタを0に戻しておく
		this.pointer = 0;
		//----------------------------------------------------------------------
		// 参照を返す
		return this;
	}

	//**************************************************************************
	/**
	 * パースした結果を返す
	 * @returns {HeaderDefinition[]}
	 */
	getData() {
		return this.headers;
	}

	//**************************************************************************
	/**
	 * ヘッダー名を指定して該当するヘッダーを取得
	 * @param {string} name 取得したいヘッダー変数名
	 * @returns {HeaderDefinition} 該当するヘッダー
	 * @throws {Error} 該当するヘッダーが見つからない場合
	 */
	find(name) {
		//----------------------------------------------------------------------
		// nameに相当するヘッダーを走査して探す
		const found = this.headers.find((header) => {
			return header.name === name;
		});
		//----------------------------------------------------------------------
		// 見つからなければ例外
		if (typeof found === "undefined") {
			throw new Error(`Invalid header "${name}" specified.`);
		}
		//----------------------------------------------------------------------
		// 結果を返す
		return found;
	}

	//**************************************************************************
	/**
	 * 基本ヘッダー定義を用意して解析
	 * @throws {Error} ヘッダー中でアルファ値0が見つかった場合
	 */
	setBaseHeaders() {
		//----------------------------------------------------------------------
		// 基本ヘッダー4種類
		this.headers.push(
			{ name: "type", pixelCount: 1, type: "rgba" },
			{ name: "headerLength", pixelCount: 1, type: "uint24" },
			{ name: "recordCount", pixelCount: 2, type: "uint48" },
			{ name: "columnCount", pixelCount: 1, type: "uint24" }
		);
		//----------------------------------------------------------------------
		// 基本ヘッダーを走査
		this.headers.forEach((header) => {
			// 念の為のvalue値チェック（不要のはず）
			if (typeof header.value === "undefined") {
				// 登録済み情報をメンバ変数に渡してvalue値取得後格納
				header.value = this.parseData(header);
				// ヘッダーに関してはnull値返却は例外扱い
				if (header.value === null) {
					throw new Error(
						`Invalid alpha value 0 for header "${header.name}"; expected 255.`
					);
				}
			}
		});
		//----------------------------------------------------------------------
		// null値返却の例外扱いはrgba指定ではチェックされないためここで個別チェック
		const type = this.find("type");
		if (type.value[3] !== 255) {
			throw new Error(
				`Invalid alpha value ${type.value[3]} for header "type"; expected 255.`
			);
		} else if (type.value[0] !== 1) {
			throw new Error(
				`Invalid R value ${type.value[0]} for header "type"; expected 1.`
			);
		}
	}

	//**************************************************************************
	/**
	 * 各カラム情報を解析してRecordAnalyzerインスタンスを生成
	 * @returns {this} 参照を返す
	 * @throws {Error} アルファ値0は例外
	 */
	setColumnHeaders() {
		//----------------------------------------------------------------------
		// 現在のポインタを取得しておく
		const pointer = this.pointer;
		//----------------------------------------------------------------------
		// カラムを格納する配列を準備
		const columns = [];
		// カラム数を取得しておく
		// columnCountはHeaderDefinition.valueなので型キャスト
		const columnCount = /** @type {number} */ (this.find("columnCount").value);
		//----------------------------------------------------------------------
		// カラム情報を走査
		for (let i = 0; i < columnCount; i += 1) {
			// RGBA値1ピクセル分を取得
			const rgba = this.parseData({ pixelCount: 1, type: "rgba" });
			// アルファ値0は例外
			if (rgba[3] === 0) {
				throw new Error(
					`Invalid alpha value 0 for column[${i}]; expected 255.`
				);
			}
			// カラム情報RGBAをRecordAnalyzerで解析する
			const analyzer = new RecordAnalyzer(rgba);
			// オフセット値は追加で与える必要があるので内部処理して新たに格納
			const offsetPixelCount = analyzer.getOffsetPixelCount();
			// オフセット値ありの場合のみ処理
			if (offsetPixelCount > 0) {
				// 以下のいずれが適切化を検討する必要あり
				// const offsetRgba = this.getRgbaData(offsetPixelCount);
				const offsetRgba = this.parseData({
					pixelCount: offsetPixelCount,
					type: "rgba"
				});
				analyzer.setOffsetPixelRgba(offsetRgba);
			}
			// 生成した解析結果を配列に格納する
			columns.push(analyzer);
		}
		//----------------------------------------------------------------------
		// レコード情報をanalyzerに格納する
		// レコード数
		const recordCount = /** @type {number} */ (this.find("recordCount").value);
		// レコード位置判定に使用するポインタを準備
		let localPointer = /** @type {number} */ (this.find("headerLength").value);
		//----------------------------------------------------------------------
		// RecordAnalyzerを走査
		columns.forEach((column) => {
			// レコード情報の総ピクセル長を決める
			const pixelLength = recordCount * column.getRecordPixelLength();
			// RGBAとしてレコード情報を取得してAnalyzerに格納
			const rawRecords = this.rawData.subarray(
				localPointer * this.ELEMENTS,
				(localPointer + pixelLength) * this.ELEMENTS
			);
			column.setRawRecords(rawRecords);
			// ポインタを進める
			localPointer += pixelLength;
		});
		//----------------------------------------------------------------------
		// カラム情報の処理が完了するので
		this.headers.push({
			name: "columns",
			pixelCount: this.pointer - pointer,
			type: "columns",
			value: columns
		});
		//----------------------------------------------------------------------
		// 参照を返す
		return this;
	}

	//**************************************************************************
	/**
	 * headerLengthに基づいて未解析の残余領域をカスタムヘッダーとして抽出
	 * @returns {this}
	 */
	setCustomHeaders() {
		//----------------------------------------------------------------------
		// ヘッダー長を決める
		const headerLength = /** @type {number} */ (this.find("headerLength").value);
		//----------------------------------------------------------------------
		// ヘッダー長とポインタ位置が異なる場合のみ処理
		if (headerLength !== this.pointer) {
			// カスタムヘッダーの分だけRGBAを取り出してヘッダーに格納
			this.headers.push({
				name: "custom",
				pixelCount: headerLength - this.pointer,
				type: "custom",
				value: this.rawData.subarray(
					this.pointer * this.ELEMENTS,
					headerLength * this.ELEMENTS
				)
			});
			// ポインタを進める
			this.pointer = headerLength;
		}
		//----------------------------------------------------------------------
		// 参照を返す
		return this;
	}

	//**************************************************************************
	/**
	 * 定義に基づいて適切なデコード処理を選択して実行（結果としてポインタが進む）
	 * @param {HeaderDefinition} header 各ヘッダー
	 * @returns {*} 返り値はヘッダーのタイプにより異なる
	 * @throws {Error} 対応するメソッドが実在しない場合（指定の誤り）
	 */
	parseData(header) {
		//----------------------------------------------------------------------
		// デコードメソッドを決める
		const type = `${header.type.slice(0, 1).toUpperCase()}${header.type.slice(1).toLowerCase()}`;
		const method = `decode${type}`;
		//----------------------------------------------------------------------
		// メソッド未定義の場合は例外
		if (typeof this[method] !== "function") {
			throw new Error(
				`Method ${method} not defined for type "${header.type}".`
			);
		}
		//----------------------------------------------------------------------
		// 処理対象のRGBA配列を取得
		// parseDataにするか検討（setColumnHeadersメソッドと同様）
		const rgbaData = this.getRgbaData(header.pixelCount);
		//----------------------------------------------------------------------
		// 処理結果を返す
		return this[method](rgbaData);
	}

	//**************************************************************************
	/**
	 * 状態をリセット
	 * @returns {this}
	 */
	dispose() {
		return this.initialize();
	}
}

//******************************************************************************
// export
export { ListPngParser };